import { useCallback, useEffect, useRef } from "react";

export interface UseEqualHeightsOptions {
  gallery_card_size?: string;
  isSplitMode?: boolean;
  align_card_sections?: boolean;
  mode?: string;
}

export interface UseEqualHeightsReturn {
  registerElement: (key: string | number, element: HTMLElement | null) => void;
  updateHeights: () => void;
  clearElements: () => void;
}

/**
 * Hook to resize Product Cards to the same height within rows
 * Supports both Gallery grid layout and Split component layout
 */
export const useEqualHeights = (options: UseEqualHeightsOptions = {}): UseEqualHeightsReturn => {
  const { gallery_card_size, isSplitMode = false, align_card_sections = false, mode } = options;
  const elementsRef = useRef<Map<any, HTMLElement>>(new Map());
  const initialAccordionHeightsRef = useRef<Map<HTMLElement, number>>(new Map());

  const isMediumGrid = gallery_card_size === "Medium grid";

  const RECOMMENDED_FLAG_HEIGHT = 32;
  const BANNER_HEIGHT = 48;

  const hasClassWithPartialName = (el: HTMLElement, partialName: string) => {
    return Array.from(el.classList).some((className) => className.includes(partialName));
  };

  const validateSplitHeights = (elements: HTMLElement[]) => {
    // Get the actual rendered heights
    const heights = elements.map((el) => el.getBoundingClientRect().height);
    const maxHeight = Math.max(...heights);

    // Apply the maximum height to all elements
    elements.forEach((el) => {
      el.style.minHeight = `${maxHeight}px`;
    });
  };

  // Memoize the height calculation logic to prevent unnecessary recalculations
  const calculateElementHeight = useCallback((el: HTMLElement) => {
    // Find all the required sections
    const accordionRef = (el as any).accordionRef;
    const featuredSection = el.querySelector('[class*="_featuredSection_"]');
    const content = el.querySelector('[class*="_content_"]');
    const bannerBottom = el.querySelector('[class*="_bannerBottom_"]');
    const ctaSection = el.querySelector('[class*="_ctaSection_"]');

    // Calculate the sum of the heights of required sections
    let totalSectionsHeight = 0;
    const cardHeight = el.getBoundingClientRect().height;
    const featuredSectionHeight = featuredSection?.getBoundingClientRect().height;

    // Featured section in medium card size is the height of the card. Add the height for small card size only
    if (featuredSectionHeight && featuredSectionHeight < cardHeight) {
      totalSectionsHeight += featuredSectionHeight;
    }

    // Get accordion height - use initial height to prevent recalculation during user interactions
    let accordionHeight = 0;
    if (accordionRef?.current) {
      const accordionElement = accordionRef.current;

      // Check if we have a cached initial height for this accordion
      if (initialAccordionHeightsRef.current.has(accordionElement)) {
        accordionHeight = initialAccordionHeightsRef.current.get(accordionElement) || 0;
      } else {
        // First time calculating - capture the initial height
        const initialHeight = accordionElement.offsetHeight;
        initialAccordionHeightsRef.current.set(accordionElement, initialHeight);
        accordionHeight = initialHeight;
      }
    }

    if (content) totalSectionsHeight += content.getBoundingClientRect().height;
    if (bannerBottom) totalSectionsHeight += bannerBottom.getBoundingClientRect().height;
    if (ctaSection) totalSectionsHeight += ctaSection.getBoundingClientRect().height;

    return totalSectionsHeight - accordionHeight;
  }, []);

  const equalizeSectionHeights = useCallback(
    (elementsInRow: HTMLElement[]) => {
      const maxContentIndex = Math.max(
        0,
        ...elementsInRow.map((el) => el.querySelectorAll('[data-eq-section^="content-"]').length)
      );

      // Equalize individual tiles within matching content sections (innermost level first)
      for (let i = 0; i < maxContentIndex; i++) {
        const contentSections = elementsInRow
          .map((el) => el.querySelector(`[data-eq-section="content-${i}"]`))
          .filter(Boolean) as HTMLElement[];

        if (contentSections.length <= 1) continue;

        const tileSets = contentSections.map((section) => {
          const container = section.querySelector('[class*="itemsContainer"]');
          return container ? (Array.from(container.children) as HTMLElement[]) : [];
        });

        const maxTiles = Math.max(0, ...tileSets.map((tiles) => tiles.length));

        for (let j = 0; j < maxTiles; j++) {
          const tilesAtIndex = tileSets.map((tiles) => tiles[j]).filter(Boolean) as HTMLElement[];
          if (tilesAtIndex.length <= 1) continue;

          const maxHeight = Math.max(...tilesAtIndex.map((t) => t.getBoundingClientRect().height));
          tilesAtIndex.forEach((t) => {
            t.style.minHeight = `${maxHeight}px`;
          });
        }
      }

      // Equalize section-level heights across cards (featured header, content blocks, CTA)
      const sectionNames: string[] = [];
      if (!isMediumGrid) sectionNames.push("featured");
      for (let i = 0; i < maxContentIndex; i++) {
        sectionNames.push(`content-${i}`);
      }
      sectionNames.push("cta");

      for (const name of sectionNames) {
        const sections = elementsInRow
          .map((el) => el.querySelector(`[data-eq-section="${name}"]`))
          .filter(Boolean) as HTMLElement[];

        if (sections.length <= 1) continue;

        const heights = sections.map((s) => s.getBoundingClientRect().height);
        const maxHeight = Math.max(...heights);

        if (name === "featured") {
          // Use paddingBottom instead of minHeight to keep images pinned to the top
          // (the featured section uses column-reverse + justify-content: center)
          sections.forEach((s, i) => {
            const diff = maxHeight - heights[i];
            if (diff > 0) {
              s.style.paddingBottom = `${diff}px`;
            }
          });
        } else {
          sections.forEach((s) => {
            s.style.minHeight = `${maxHeight}px`;
          });
        }
      }
    },
    [isMediumGrid]
  );

  const updateHeights = useCallback(() => {
    requestAnimationFrame(() => {
      // Get only visible elements
      const visibleElements = Array.from(elementsRef.current.values()).filter((el) => el.offsetParent !== null);

      if (visibleElements.length === 0) return;

      // Reset all heights and force recalculation
      visibleElements.forEach((el) => {
        el.style.minHeight = "";
        el.style.marginTop = "";
        if (align_card_sections) {
          el.querySelectorAll<HTMLElement>("[data-eq-section]").forEach((section) => {
            section.style.minHeight = "";
            section.style.paddingBottom = "";
          });
          el.querySelectorAll<HTMLElement>('[data-eq-section^="content-"]').forEach((section) => {
            const container = section.querySelector('[class*="itemsContainer"]');
            if (container) {
              Array.from(container.children).forEach((child) => {
                (child as HTMLElement).style.minHeight = "";
              });
            }
          });
        }
      });

      // Wait for layout to settle after style reset (double rAF ensures full reflow)
      requestAnimationFrame(() => {
        const rowGroups = new Map<number, HTMLElement[]>();

        // First pass: group elements by their top position
        for (const el of visibleElements) {
          const topPosition = Math.round(el.getBoundingClientRect().top);
          const TOLERANCE = 50;

          let closestRow: number | null = null;
          let minDiff = TOLERANCE + 1;

          Array.from(rowGroups.entries()).forEach(([existingTop, elements]) => {
            const diff = Math.abs(existingTop - topPosition);
            if (diff <= TOLERANCE && diff < minDiff) {
              closestRow = existingTop;
              minDiff = diff;
            }
          });

          if (closestRow !== null) {
            rowGroups.get(closestRow)!.push(el);
          } else {
            rowGroups.set(topPosition, [el]);
          }
        }

        if (align_card_sections) {
          // Equalize section heights within each row (tiles, featured, content blocks, CTA)
          Array.from(rowGroups.values()).forEach((elementsInRow) => {
            if (isSplitMode && elementsInRow.length !== 2) return;
            if (!isSplitMode && elementsInRow.length === 1) return;
            equalizeSectionHeights(elementsInRow);
          });
        }

        // Second pass: set the heights for each row group
        Array.from(rowGroups.values()).forEach((elementsInRow, index) => {
          // For split mode, only process if we have exactly 2 elements (both sides of split)
          if (isSplitMode && elementsInRow.length !== 2) return;

          // For gallery mode, check for recommended flags (single-program only)
          if (!isSplitMode && elementsInRow.length === 1) {
            if (mode === "multi-program") return;

            for (const el of elementsInRow) {
              const hasRecommended = hasClassWithPartialName(el, "recommended");
              // If the card is recommended and not the first card, use 24px margin. If it's the first card, use 32px margin.
              if (hasRecommended) el.style.marginTop = index > 0 ? "24px" : `${RECOMMENDED_FLAG_HEIGHT}px`;
            }

            return;
          }

          const hasBanner = elementsInRow.some((el: HTMLElement) => hasClassWithPartialName(el, "cardWithBanner"));
          const hasRecommended = elementsInRow.some((el: HTMLElement) => hasClassWithPartialName(el, "recommended"));
          const hasBannerAndRecommended = elementsInRow.some(
            (el: HTMLElement) =>
              hasClassWithPartialName(el, "cardWithBanner") && hasClassWithPartialName(el, "recommended")
          );

          // Calculate max height in a single pass
          let maxRowHeight = 0;
          for (const el of elementsInRow) {
            const elementHeight = calculateElementHeight(el);
            maxRowHeight = Math.max(maxRowHeight, elementHeight);
          }

          for (const el of elementsInRow) {
            const hasBannerOnCard = hasClassWithPartialName(el, "cardWithBanner");
            const isRecommendedOnCard = hasClassWithPartialName(el, "recommended");

            let marginTop = 0;

            // Case 1: Row has at least one card with both banner and recommended
            if (hasBannerAndRecommended) {
              const FLAG_HEIGHT = index === 0 ? 0 : RECOMMENDED_FLAG_HEIGHT;
              if (hasBannerOnCard && isRecommendedOnCard) {
                marginTop = FLAG_HEIGHT;
              } else {
                marginTop = hasBannerOnCard ? FLAG_HEIGHT : FLAG_HEIGHT + BANNER_HEIGHT;
              }
            }
            // Case 2: Row has at least one banner (but no cards with both banner and recommended)
            else if (hasBanner) {
              marginTop = hasBannerOnCard ? 0 : BANNER_HEIGHT;
              marginTop = isMediumGrid ? 0 : marginTop;
            }
            // Case 3: Row has at least one recommended flag but no banners
            else if (hasRecommended && index >= 1) {
              marginTop = isRecommendedOnCard ? RECOMMENDED_FLAG_HEIGHT : 0;
            }
            // Calculate row height consistently for all grid types
            let rowHeight = maxRowHeight;
            if (isMediumGrid) {
              marginTop = 0;
              rowHeight = maxRowHeight + (hasBanner ? BANNER_HEIGHT : 0);
            } else {
              rowHeight = maxRowHeight + (hasBannerOnCard ? BANNER_HEIGHT : 0);
            }

            el.style.marginTop = `${marginTop}px`;
            el.style.minHeight = `${rowHeight}px`;
          }

          // For split mode, schedule a validation pass to ensure exact height matching
          if (isSplitMode && elementsInRow.length === 2) {
            const elementsToValidate = [...elementsInRow];

            // Re-evaluate heights to ensure exact height matching
            Promise.resolve().then(() => {
              validateSplitHeights(elementsToValidate);
            });
          }
        });
      });
    });
  }, [isMediumGrid, isSplitMode, mode, calculateElementHeight, equalizeSectionHeights]);

  useEffect(() => {
    // Initial calculation on component mount
    updateHeights();

    // Debounced window resize handler for layout changes (like media queries)
    let resizeTimeout: NodeJS.Timeout;
    const handleWindowResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateHeights();
      }, 100); // Wait 100ms after resize stops
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
      clearTimeout(resizeTimeout);
    };
  }, [updateHeights]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof ResizeObserver === "undefined") return;

    let didTrigger = false;
    const observer = new ResizeObserver(() => {
      if (didTrigger) return;

      const visibleElements = Array.from(elementsRef.current.values()).filter((el) => el.offsetParent !== null);
      if (visibleElements.length === 0) return;

      didTrigger = true;
      requestAnimationFrame(() => {
        updateHeights();
        observer.disconnect();
      });
    });
    observer.observe(document.body);

    return () => observer.disconnect();
  }, [updateHeights]);

  const registerElement = useCallback((key: string | number, element: HTMLElement | null) => {
    if (element) {
      elementsRef.current.set(key, element);
    } else {
      const existingElement = elementsRef.current.get(key);
      if (existingElement) {
        elementsRef.current.delete(key);
        // Clean up cached accordion height when element is unregistered
        initialAccordionHeightsRef.current.delete(existingElement);
      }
    }
  }, []);

  const clearElements = useCallback(() => {
    elementsRef.current.clear();
  }, []);

  return { registerElement, updateHeights, clearElements };
};
